import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ShieldAlert, Award, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/academy`;

export default function CertificateVerifyPage() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/verify/${certId}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData({ valid: false, reason: 'Erreur réseau' }))
      .finally(() => setLoading(false));
  }, [certId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/40 to-slate-950 flex items-center justify-center px-4 py-10 text-white">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8" data-testid="verify-page">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-full bg-emerald-500/20 items-center justify-center mb-3">
            <Award size={28} className="text-yellow-300" />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-1">JoSmartTech Academy</div>
          <h1 className="text-2xl font-bold">Vérification du certificat</h1>
        </div>

        {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-emerald-400" size={32} /></div>}

        {!loading && data?.valid && (
          <div data-testid="verify-valid">
            <div className="flex items-center gap-3 bg-emerald-500/15 border border-emerald-400/40 rounded-2xl p-4 mb-5">
              <ShieldCheck size={28} className="text-emerald-300 flex-shrink-0" />
              <div>
                <div className="font-bold text-emerald-200">Certificat authentique</div>
                <div className="text-xs text-emerald-300/70">Signature cryptographique validée</div>
              </div>
            </div>

            <Row label="Délivré à" value={data.name} highlight />
            <Row label="Formation" value={data.formation} />
            {data.modules_count && <Row label="Modules" value={data.modules_count} />}
            <Row label="Leçons validées" value={`${data.lessons_completed} / ${data.lessons_total}`} />
            <Row label="N° de série" value={data.serial} mono />
            <Row label="Date de délivrance" value={new Date(data.issued_at).toLocaleDateString('fr-FR', { dateStyle: 'long' })} />

            <div className="mt-6 text-center text-xs text-white/40">
              Ce certificat a été émis par JoSmartTech et signé numériquement.<br />
              Pour toute question : <a className="text-emerald-300 hover:underline" href="mailto:contact@josmarttech.net">contact@josmarttech.net</a>
            </div>
          </div>
        )}

        {!loading && data && !data.valid && (
          <div className="text-center" data-testid="verify-invalid">
            <div className="inline-flex w-16 h-16 rounded-full bg-red-500/20 items-center justify-center mb-3">
              <ShieldAlert size={32} className="text-red-300" />
            </div>
            <div className="font-bold text-red-200 text-lg">Certificat non vérifiable</div>
            <div className="text-sm text-white/60 mt-2">{data.reason || "Ce certificat n'a pas pu être authentifié."}</div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Row({ label, value, highlight, mono }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2.5 text-sm">
      <span className="text-white/50 text-xs uppercase tracking-wider">{label}</span>
      <span className={`${highlight ? 'text-yellow-300 font-bold text-base' : 'text-white'} ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}
